import {CALENDAR_DAYS_OF_WEEK, CALENDAR_DAYS_OF_WEEK_ORDINAL} from "@/constants";
import {DayOfWeekType} from "@/store/modules/hcs/calendar/model";
import {DateTime} from "luxon";

/**
 * 曜日の数値型を曜日に変換します
 *
 * @param ordinal 曜日の数値型
 */
export function ordinalToWday(ordinal: number) {
  for(const [key, _ordinal] of Object.entries(CALENDAR_DAYS_OF_WEEK_ORDINAL)) {
    if(ordinal === _ordinal) {
      return key as keyof typeof CALENDAR_DAYS_OF_WEEK_ORDINAL;
    }
  }
  throw new Error(`Unknown weekday ordinal: ${ordinal}`)
}

/**
 * 曜日の配列をソートします
 *
 * @param daysOfWeek 曜日の配列
 */
export function sortWday(daysOfWeek: DayOfWeekType[]) {
  return daysOfWeek.sort((a, b) => {
    return CALENDAR_DAYS_OF_WEEK_ORDINAL[a] - CALENDAR_DAYS_OF_WEEK_ORDINAL[b];
  });
}

/**
 * 指定された日付の曜日が月の何番目かを取得します
 *
 * @param date 日付
 */
export function nthOfWday(date: DateTime): number {
  return Math.floor(date.day / 7) + 1;
}

/**
 * 連続する曜日の範囲を表現します
 */
class WdayRange {
  /** 最大値 日曜日に相当 */
  static MAX = 7;

  /** 最小値 月曜日に相当 */
  static MIN = 1;

  /** 開始曜日 */
  readonly start: number;

  /** 終了曜日 */
  readonly end: number;

  /**
   * コンストラクタ
   *
   * @param start 開始曜日
   * @param end 終了曜日
   */
  constructor(start: number, end: number) {
    this.start = (start - 1) % 7 + 1;
    this.end = (end - 1) % 7 + 1;
  }

  /**
   * 連続した曜日になる場合、この曜日の範囲と指定された曜日を結合した WdayRange を生成して返します
   *
   * @param weekday 新しい曜日
   */
  public addIfAdjacent(weekday: number) {
    if(this.start - 1 === weekday) {
      return new WdayRange(weekday, this.end);
    }
    if(this.start === WdayRange.MIN && weekday === WdayRange.MAX) {
      return new WdayRange(WdayRange.MAX, this.end);
    }
    if(this.end + 1 === weekday) {
      return new WdayRange(this.start, weekday);
    }
    if(this.end === WdayRange.MAX && weekday === WdayRange.MIN) {
      return new WdayRange(this.start, WdayRange.MIN);
    }
    return this;
  }

  /**
   * この曜日範囲がすべての曜日を表現している場合 true そうでない場合 false を返します
   */
  get isAll() {
    return this.start === WdayRange.MIN && this.end === WdayRange.MAX;
  }

  /**
   * 日〜月曜日をまたがない曜日範囲の場合 true そうでない場合 false を返します
   */
  get isForward() {
    return this.start <= this.end;
  }

  /**
   * 文字列表現を取得します
   */
  toString() {
    const so = ordinalToWday(this.start), eo = ordinalToWday(this.end);
    if(this.start === this.end) {
      return `${CALENDAR_DAYS_OF_WEEK[so]}曜日`;
    } else {
      return `${CALENDAR_DAYS_OF_WEEK[so]} 〜 ${CALENDAR_DAYS_OF_WEEK[eo]}曜日`;
    }
  }

  /**
   * 二つの WdayRange に交叉する部分がある場合、和集合を返します。そうでない場合 null を返します
   *
   * @param a 1 つめの WdayRange
   * @param b 2 つめの WdayRange
   */
  static union(a: WdayRange, b: WdayRange): WdayRange | null {
    if(a.isAll || b.isAll) {
      return new WdayRange(this.MIN, this.MAX);
    }

    const af = a.isForward, bf = b.isForward;
    let as = a.start, ae = a.end, bs = b.start, be = b.end;
    if(!af && bf) {
      if(bs <= as || ae <= be) ae += 7; else as -= 7;
    } else if(af && !bf) {
      if(as <= bs || be <= ae) be += 7; else bs -= 7;
    } else if(!af && !bf) {
      return new WdayRange(Math.min(as, bs), Math.max(ae, be));
    }

    if(as <= be && bs <= ae) {
      return new WdayRange(Math.min(as, bs), Math.max(ae, be));
    } else {
      return null;
    }
  }
}

/**
 * 連続しない曜日の範囲を表現します
 */
export class WdayRanges {
  /** 連続する曜日範囲 WdayRange の配列 */
  private ranges: WdayRange[] = [];

  /**
   * 数値表現の曜日の配列から WdayRanges を生成します
   *
   * @param values
   */
  static fromOrdinals(values: number[]): WdayRanges {
    const r = new WdayRanges();
    for(const n of values) {
      r.add(n);
    }
    return r;
  }

  /**
   * この WdayRanges に新しい曜日を追加します
   *
   * @param weekday 曜日
   */
  add(weekday: number) {
    if(this.ranges.length === 0) {
      this.ranges.push(new WdayRange(weekday, weekday));
      return;
    }

    const ranges = this.ranges.map(r => r.addIfAdjacent(weekday));
    ranges.push(new WdayRange(weekday, weekday));
    for(let i=0; i<ranges.length; i++) {
      for(let j=i+1; j<ranges.length; j++) {
        const u = WdayRange.union(ranges[i], ranges[j]);
        if(u) {
          ranges.splice(i, 1, u);
          ranges.splice(j, 1);
          j--;
        }
      }
    }
    this.ranges = ranges;
  }

  /**
   * 文字列表現を取得します
   */
  toString() {
    return this.ranges.map(r => r.toString()).join(', ');
  }
}
